import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Typography from '@tiptap/extension-typography'
import Placeholder from '@tiptap/extension-placeholder'
import { useEffect, useRef, useMemo, useCallback } from 'react'
import { marked } from 'marked'

interface ContentFFoolscapContentProps {
  foolscapMarkdown: string | null
  foolscapMarkdownAnnotated?: string | null
  showAnnotations?: boolean
  streaming?: boolean
  onCardClick?: (cardId: string) => void
}

// Configure marked to allow HTML passthrough for annotations
marked.use({
  breaks: true,
  gfm: true,
})

export function ContentFFoolscapContent({
  foolscapMarkdown,
  foolscapMarkdownAnnotated,
  showAnnotations = true,
  streaming = false,
  onCardClick,
}: ContentFFoolscapContentProps) {
  const editorContainerRef = useRef<HTMLDivElement>(null)
  const prevContentRef = useRef<string>('')

  // For clean markdown (no annotations), use TipTap
  // For annotated markdown, render directly with dangerouslySetInnerHTML
  // This is because TipTap strips <ins>, <del>, <span> tags
  const useDirectRender = showAnnotations && foolscapMarkdownAnnotated

  // Convert clean markdown to HTML for Tiptap
  const htmlContent = useMemo(() => {
    if (!foolscapMarkdown) return ''
    return marked.parse(foolscapMarkdown, { async: false }) as string
  }, [foolscapMarkdown])

  // Convert annotated markdown to HTML for direct rendering
  const annotatedHtmlContent = useMemo(() => {
    if (!foolscapMarkdownAnnotated) return ''
    // Parse markdown, preserving the <ins>, <del>, <span> HTML tags
    return marked.parse(foolscapMarkdownAnnotated, { async: false }) as string
  }, [foolscapMarkdownAnnotated])

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4, 5, 6] },
        horizontalRule: {},
        bulletList: {},
        listItem: {},
      }),
      Placeholder.configure({
        placeholder: 'Your Foolscap document...',
      }),
      Link.configure({
        openOnClick: false,
      }),
      Typography,
    ],
    content: htmlContent,  // Initialize with HTML content
    editable: false,  // Read-only (document is built by AI)
    editorProps: {
      attributes: {
        class: 'prose-z outline-none min-h-full',
      },
    },
    immediatelyRender: false,
  }, [])

  // Update editor content when markdown changes (only for clean view)
  useEffect(() => {
    if (!editor || useDirectRender) return

    const contentChanged = prevContentRef.current !== htmlContent
    prevContentRef.current = htmlContent

    if (!contentChanged) return

    editor.commands.setContent(htmlContent || '', { emitUpdate: false })
  }, [editor, htmlContent, useDirectRender])

  // Handle clicks on card links (card:UUID format)
  const handleClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement
    const link = target.closest('a')
    if (!link) return

    const href = link.getAttribute('href')
    if (!href) return

    // Check for card: protocol
    if (href.startsWith('card:')) {
      e.preventDefault()
      e.stopPropagation()
      const cardId = href.slice(5) // Remove 'card:' prefix
      if (cardId && onCardClick) {
        onCardClick(cardId)
      }
    }
  }, [onCardClick])

  // Highlight last block during streaming
  useEffect(() => {
    const container = editorContainerRef.current
    if (!container) return

    const blocks = container.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li')

    if (!streaming) {
      blocks.forEach(el => el.classList.remove('streaming-line-highlight'))
      return
    }

    blocks.forEach(el => el.classList.remove('streaming-line-highlight'))

    const lastBlock = blocks[blocks.length - 1]
    if (lastBlock) {
      lastBlock.classList.add('streaming-line-highlight')
    }

    return () => {
      blocks.forEach(el => el.classList.remove('streaming-line-highlight'))
    }
  }, [streaming, htmlContent, annotatedHtmlContent])

  if (!foolscapMarkdown) return null

  // Render annotated content directly (bypasses TipTap to preserve <ins>/<del>/<span>)
  if (useDirectRender) {
    return (
      <div className="px-4 py-4">
        <div
          className="prose-z min-h-full"
          ref={editorContainerRef}
          onClick={handleClick}
          dangerouslySetInnerHTML={{ __html: annotatedHtmlContent }}
        />
      </div>
    )
  }

  // Render clean content via TipTap
  return (
    <div className="px-4 py-4">
      <div className="min-h-full" ref={editorContainerRef} onClick={handleClick}>
        <EditorContent editor={editor} className="min-h-full" />
      </div>
    </div>
  )
}
